/**
 * `usePageBoundaries` — ResizeObserver/rAF/state shell that measures the
 * editor's rendered content and returns the Y positions (CSS px) where the
 * SERVER-exported PDF starts a new page, so `PageBoundaryOverlay` can draw a
 * "see the pages" indicator without the editor's DOM ever growing a real gap
 * (RECUR-27304).
 *
 * Delta v3: the actual page-start MODEL — the DOM-measuring glue
 * (`getTopLevelBlocks`, `findTableElement`, `buildAtomicUnits`, …) and the
 * pure pagination fit-loop — now lives in `../exporters/pageStartUnits.ts` +
 * `../exporters/pageBreakModel.ts`, moved out so the SAME model output can
 * also be consumed by the server exporter (`exportToPdfServer.ts`), which
 * injects forced page breaks at the model's page-starts so the exported PDF
 * obeys the model exactly (see that file). This hook is now JUST the
 * ResizeObserver/rAF/state shell (D3): find the content element, call
 * `computePageBoundaries`, and re-render only when the derived result
 * actually changes. Public signature + return shape UNCHANGED — `PlateEditor.tsx`
 * and `PageBoundaryOverlay` are untouched by this refactor.
 *
 * Measurement: a ResizeObserver on the `[data-testid="plate-editor-content"]`
 * element found inside the passed container element (the same selector both
 * PDF exporters key on) fires on every edit/import/resize-driven reflow. The
 * callback is rAF-throttled (coalesces a burst of RO notifications into one
 * measurement per frame) and only calls `setState` when the derived result
 * actually changes — a long document re-renders the overlay only when a
 * boundary moves or a page is added/removed, not on every keystroke.
 *
 * Takes the container as a plain element (not a `RefObject`) so the caller
 * holds it in state via a callback ref: `PlateErrorBoundary`'s crash-recovery
 * remount swaps in a brand-new DOM node, and only a STATE change (unlike a
 * stable ref object, whose identity never changes) makes this hook's effect
 * re-run and re-attach the observer to the new node instead of staying
 * attached to the now-detached one.
 *
 * `enabled=false` (the default every non-document PlateEditor surface uses)
 * short-circuits to an empty result and never creates an observer.
 *
 * RECUR-27304 gap-display: this hook is now also the SOLE place that WRITES
 * the page-gap styling onto the live DOM (`applyPageGapStyles`, below) — see
 * `pageStartUnits.ts`'s module doc for the normalized/live coordinate-space
 * split this depends on. Applied as a CSS custom property + class only,
 * never a Slate transform, and re-applied on EVERY measurement pass (not
 * gated behind the "did the boundaries change" check) so a Plate/React
 * re-render that drops the inline style/class self-heals on the very next
 * pass instead of staying wrong until some unrelated future edit happens to
 * re-trigger a measurement.
 */
import { useEffect, useRef, useState } from 'react';
import plateEditorStyles from '../PlateEditor.module.scss';
import { PNO_LOGO_MAX_HEIGHT_IN, PNO_LOGO_GAP_BELOW_IN } from '../exporters/pageGeometry';
import {
    computePageBoundaries,
    computePageGapTargets,
    computePageEndGapTargets,
    findTableElement,
    getContentColumnBox,
    getInjectedGapPx,
    getInjectedEndGapPx,
    getSlateCellChildren,
    getTopLevelBlocks,
    EMPTY_BOUNDARIES,
    PAGE_GAP_CSS_VAR,
    PAGE_END_GAP_CSS_VAR,
    type PageBoundaries,
    type PageGapTarget,
    type PageEndGapTarget,
} from '../exporters/pageStartUnits';
import { measureNormalizedContentBottomPx, reportPaginationMeasurement } from '../exporters/paginationTelemetry';

export type { PageBoundaries };
export { computePageBoundaries };

/**
 * RECUR-27604 approved visual spec (mock v2) — the PAINTED display slot a
 * block/row-kind seam reserves for the band, grown from the model's own 66px
 * reservation (`pageGeometry.ts`'s `PNO_HEADER_BAND_PX`, which stays 66 and
 * MUST NOT change — it is the SERVER export's own per-page capacity
 * subtraction) so the 48px logo can sit vertically centered with 21px of air
 * above and below it, instead of flush against the slot's own top. Legal
 * because this is purely DISPLAY space: `stripPageGapStyles` removes it
 * before every measurement (same as any other seam gap), and the export
 * path's own normalization (`pageStartUnits.ts`'s `buildAtomicUnits` /
 * `getInjectedGapPx`) subtracts back whatever is ACTUALLY painted on the live
 * DOM, never a hardcoded 66 — so painting 90 here instead of 66 changes
 * nothing the model or the PDF ever sees (see that file's module doc's
 * NORMALIZED/LIVE coordinate-space split). Lives in this hook (the ONLY
 * place that paints the addend) and is imported by `PageBreakSlotOverlay`
 * (the only other place that needs it, to size its mask/logo-centering
 * math) so the two can never drift apart.
 *
 * RECUR-27604 §11 (always-on full-width page-start break, rev 6): renamed
 * from `EDITOR_BAND_SLOT_PX` — the slot's job is no longer "hold the PNO
 * band" alone, it is now the page-break chrome itself (a hosted seam must
 * read like the PDF's physical break — closing edge, full-width clean gap,
 * opening edge) and renders on EVERY page-gap document regardless of the
 * logo flags; the logo `<img>` is just one optional thing that can sit
 * inside it. One value for both cases (never a smaller no-logo slot): the
 * seam rhythm must not jump when a PNO flips the logo flag, and `90 − 2 − 1
 * = 87` (the mask height, see `PageBreakSlotOverlay`) stays one formula
 * either way.
 */
export const EDITOR_PAGE_BREAK_SLOT_PX = 90;

/**
 * RECUR-27604 §13.3 — the page-END padding painted on the LAST hostable unit
 * before a boundary (the page ABOVE it), mirroring `EDITOR_PAGE_BREAK_SLOT_PX`
 * on the page BELOW. 40px: big enough to read as a page-bottom margin rather
 * than ordinary paragraph rhythm (body line-height 24px, `h1` margin-top
 * 24px, `table` margin 16px — anything <= 24 is indistinguishable from
 * normal spacing), roughly 0.42in at 96px/in (a plausible bottom-margin
 * share), and at most half of `EDITOR_PAGE_BREAK_SLOT_PX` per the approved
 * visual spec. Always-on wherever `showPageGaps` is (never logo-gated,
 * §13.3(c)) — same reasoning as the top slot (§11.1): a page-bottom margin
 * that appeared only for logo PNOs would make identical documents paginate-
 * looking differently per config.
 */
export const EDITOR_PAGE_END_SLOT_PX = 40;

// RECUR-27604 approved visual spec (mock v2) — page 1 wants 28px of clear
// space above the logo, then the 48px logo, then the SAME 18px of air the
// PDF export itself uses before the first block: 94px total. The card's own
// built-in top inset (72px padding-top + 1px border, PlateEditor.module.scss)
// only holds 73px, so the shortfall is injected as DISPLAY-ONLY space on the
// FIRST top-level block, via the exact same strip-before-measure mechanism
// (stripPageGapStyles/applyPageGapStyles) every other page-start slot already
// uses below — `buildAtomicUnits`' per-block `getInjectedGapPx` accounting
// already covers block 0 like any other block (its `topShift` map is built
// via `blocks.map(...)` over every index), so this is exactly as
// model-invisible as a seam gap.
export const PAGE1_CLEAR_ABOVE_LOGO_PX = 28;
const CONTENT_TOP_INSET_PX = 73; // .content's 72px padding-top + 1px border
const PAGE1_TOTAL_SPACE_PX = PAGE1_CLEAR_ABOVE_LOGO_PX + PNO_LOGO_MAX_HEIGHT_IN * 96 + PNO_LOGO_GAP_BELOW_IN * 96; // 94
/** Exported (like `EDITOR_PAGE_BREAK_SLOT_PX`) so tests pin against the derived spec value directly instead of a hardcoded magic number. */
export const PAGE1_INJECTED_GAP_PX = Math.max(0, PAGE1_TOTAL_SPACE_PX - CONTENT_TOP_INSET_PX); // 21

/**
 * One page-break slot `PageBreakSlotOverlay` should paint (RECUR-27604 §11,
 * renamed from `PnoLogoBand`: the slot's job is the page-start CHROME itself
 * — closing edge, full-width clean gap, opening edge — a PNO band is only an
 * optional passenger inside it). `usePageBoundaries` only ever emits
 * `'block'`/`'row'` entries — `'page1'` is added by the overlay itself
 * (page 1's slot sits in the card's own top padding: no injected slot, no
 * host table) so it can fold both sources into one uniformly-typed,
 * uniformly-rendered list. `hostLeft`/`hostWidth` (contentEl-relative, the
 * SAME coordinate space `top` is already in) are set ONLY for `'row'`: the
 * mid-table slot is cell padding INSIDE the table, so the synthetic table
 * edges need the owning `<table>`'s own box to bound them — a
 * `'block'`/`'page1'` slot already sits in real blank page space and needs no
 * host box or edges at all. `pageLeft`/`pageWidth` (also `'row'`-only, same
 * coordinate space) are the CONTENT COLUMN's own box — measured once per pass
 * from `contentEl` (§11.3) — for the mask, which spans the full page width
 * rather than just the host table's: a narrower table would otherwise leave
 * grid lines/borders visible beside it at the seam.
 *
 * RECUR-27604 §13.3 (page-end padding): `endSlot` is the mirror-image slot
 * painted on the LAST hostable unit of the page ABOVE this boundary (never
 * the page below, which `kind`/`hostLeft`/`hostWidth`/`pageLeft`/`pageWidth`
 * above continue to describe) — present whenever `computePageEndGapTargets`
 * emitted a target for this boundary, independent of whether the TOP slot
 * itself is hostable (a `'line'`/crossed/un-hostable top can still sit right
 * below a perfectly hostable page-end, and vice versa — the two hostability
 * checks are independent). `kind: 'endOnly'` marks a boundary with an end
 * slot but NO top slot: nothing from the existing `kind`-driven rendering
 * (mask/edges/logo for the page BELOW) should paint there at all.
 */
export interface PageBreakSlot {
    top: number;
    kind: 'page1' | 'block' | 'row' | 'endOnly';
    hostLeft?: number;
    hostWidth?: number;
    pageLeft?: number;
    pageWidth?: number;
    endSlot?: { kind: 'block' | 'row'; hostLeft?: number; hostWidth?: number };
    /**
     * RECUR-27852 — the end host's rendered anchor (contentEl-relative px, the
     * host's own border-BOTTOM incl. its injected bottom slot). The end-side
     * pieces (mask/edge/shield) anchor here rather than at `top − 40`, because
     * on a drifted seam the end host can render well away from `top`. `top`
     * itself already carries the TOP host's MEASURED anchor (see the
     * `liveBoundaries` override in `measure`), so the top-side pieces need no
     * extra field — they stay pinned to `top`. `undefined` with no measurable
     * layout (jsdom) — the overlay then falls back to `top − 40`, so every
     * existing band-shape test stays green. See `measureEndSlotAnchor`.
     */
    endSlotSeam?: number;
}

/**
 * `usePageBoundaries`' own return shape (RECUR-27604 §8, N-5; §11): `PageBoundaries`
 * plus `bands` — one `PageBreakSlot` per boundary that actually received an
 * injected slot (a 'block'-kind start, always; a 'row'-kind start, only when
 * hostable — §8.5). `PageBreakSlotOverlay` renders this array blindly (slot <=>
 * injected slot, by construction — the hostability predicate is evaluated
 * exactly once, in `computePageGapTargets`, never re-derived here). `bandTops`
 * is kept alongside it (`bands.map((b) => b.top)`) purely so anything that
 * only ever needed the plain Y values — this file's own regression suite
 * pins a lot of these — doesn't need touching for a change that's additive
 * by construction. A HOOK-only, DOM-dependent concept: `computePageBoundaries`
 * (the pure model function, also used directly by tests) has no live `<tr>`
 * to check `row.style.height` (or measure a host `<table>`) against, so
 * neither field lives on the shared `PageBoundaries` type.
 *
 * `pageLeft`/`pageWidth` (RECUR-27604 §13.1): the SAME content-column box
 * `bands[i].pageLeft`/`pageWidth` already carry for a `'row'`-kind slot's
 * mask — exposed here TOP-LEVEL too so `PageBreakSlotOverlay` can size a hit
 * shield for a `'page1'`/`'block'`/`'endOnly'` slot (none of which carry a
 * per-band `pageLeft`/`pageWidth` today — adding it to every band would
 * change several committed exact-equality fixtures for no behavioural gain,
 * since the value is identical for every band in one measurement pass
 * anyway).
 */
export interface PageBoundariesWithBand extends PageBoundaries {
    bandTops: number[];
    bands: PageBreakSlot[];
    pageLeft: number;
    pageWidth: number;
}

const EMPTY_BOUNDARIES_WITH_BAND: PageBoundariesWithBand = { ...EMPTY_BOUNDARIES, bandTops: [], bands: [], pageLeft: 0, pageWidth: 0 };

const CONTENT_SELECTOR = '[data-testid="plate-editor-content"]';

const PAGE_GAP_REWRITE_EPSILON_PX = 1;

// Text-only edits coalesce onto this idle timer instead of measuring every
// animation frame. A full measure walks every block (and every table row/cell
// on a CAM) for geometry and re-paints gap styles, so the per-frame cadence
// made typing janky. A seam only MOVES once reflow changes a line's height,
// which the user cannot perceive inside this window anyway. Element-level
// structural edits (new/removed <p>/<tr>/…) and container WIDTH resizes stay
// immediate — see scheduleMeasure below. Slate typing often replaces the text
// NODE (childList of #text) rather than mutating characterData; that must
// debounce too or a 4k-cell CAM pays 200–500ms per key.
const TEXT_EDIT_MEASURE_IDLE_MS = 180;

function countElementNodes(list: { length: number; [index: number]: Node } | null | undefined): number {
    if (!list || typeof list.length !== 'number') return 0;
    let n = 0;
    for (let i = 0; i < list.length; i += 1) {
        if (list[i]?.nodeType === Node.ELEMENT_NODE) n += 1;
    }
    return n;
}

// A CAM import/append inserts dozens of tables in one burst. Measuring that
// immediately walks every block+row for geometry — seconds on a 4k-cell doc.
// Debounce the same idle window as typing; a single row/paragraph insert
// stays immediate.
const BULK_ELEMENT_MEASURE_IDLE = 8;

// onepager-pdf-orphan-blank-page: a fixed-height host <tr> only partially grows
// under cell padding (its own slack absorbs the rest), so applyPageGapStyles ALSO
// grows the row's inline `height` by exactly its slot. Delivered as
// `calc(var(--slot-base-height) + var(--page-gap, 0px))`: --slot-base-height holds
// the row's ORIGINAL inline height, so under the SERVER export's
// `--page-gap: 0px !important` (exportToPdfServer.ts) the calc collapses straight
// back to the base — the PDF row is byte-identical, no export-side change needed.
// stripPageGapStyles restores the plain base height before every measure, so the
// page-break model never sees the grown value.
const SLOT_BASE_HEIGHT_VAR = '--slot-base-height';

/** Strips one CSS-var/class pair off `el` if painted — shared by the top-gap and end-gap branches of `stripPageGapStyles`. */
function stripOneGapVar(el: HTMLElement, cssVar: string, read: (el: HTMLElement) => number, cssClass: string): void {
    if (read(el) === 0) return;
    el.style.removeProperty(cssVar);
    el.classList.remove(cssClass);
}

// RECUR-27604 exhaustive-strip fix — every class `applyPageGapStyles` can
// EVER paint a `--page-gap`/`--page-end-gap` pair alongside, across every
// role (block/row/cell-child). `sweepStripResidualGapStyles` below removes
// whichever ones are present without needing to know an element's role —
// removing a class an element never had is a no-op — so it stays correct
// even if a future vehicle adds a new role/class pair here without a
// matching update to the structured traversal.
const TOP_GAP_CLASSES = [plateEditorStyles.pageGapBefore, plateEditorStyles.pageRowGapBefore, plateEditorStyles.pageCellSegGapBefore];
const END_GAP_CLASSES = [plateEditorStyles.pageEndGapAfter, plateEditorStyles.pageRowEndGapAfter, plateEditorStyles.pageCellSegEndGapAfter];

/**
 * RECUR-27604 belt-and-suspenders (proven live-DOM bug): the structured walk
 * below (`getTopLevelBlocks` -> `findTableElement` -> `rows`/cells) assumes a
 * fixed block/table/row/cell-child shape. On the real deployed app a
 * residual `--page-gap`/`--page-end-gap` was observed surviving that walk on
 * SOME element it doesn't reach — inflating the measured content by one
 * `EDITOR_PAGE_BREAK_SLOT_PX` and settling the fit-loop at a stable-but-EARLY
 * break (2 rows before the true, gap-free break the export itself uses).
 * This sweep is the guarantee: it walks the WHOLE content subtree once,
 * keyed purely off "is a gap var currently painted here" (via the SAME
 * `getInjectedGapPx`/`getInjectedEndGapPx` readers every other strip/apply
 * path already trusts) rather than off the DOM shape a vehicle is expected to
 * have — so it clears a residual on ANY element, known role or not. Read via
 * inline style (not classList) because the var, not the class, is what
 * `buildAtomicUnits`/geometry actually measures back — a class alone,
 * without the var, paints nothing.
 * Idempotent and strictly safe to run after the structured traversal:
 * clearing an already-gap-free element is a no-op, and `applyPageGapStyles`
 * repaints every correct spacer again immediately after in the SAME pass.
 */
function sweepStripResidualGapStyles(contentEl: HTMLElement): void {
    // Scoped to elements that could actually carry a residual, rather than every
    // descendant: `getInjectedGapPx`/`getInjectedEndGapPx` read `el.style` — INLINE
    // style only, never computed — so nothing without a `style` attribute can hold a
    // gap var, and the marker class is matched by its own selector. Same element set
    // this ever mutated, minus the whole-tree walk that dominated a measure on a long
    // document.
    // Defensive on the class name: were it ever dropped from the SCSS module the
    // interpolation would silently become `.undefined` and match nothing, quietly
    // narrowing the sweep. Fall back to the inline-style half alone instead.
    const segRowActiveClass = plateEditorStyles.pageCellSegRowActive;
    const elements = contentEl.querySelectorAll<HTMLElement>(segRowActiveClass ? `[style], .${segRowActiveClass}` : '[style]');
    elements.forEach((el) => {
        if (getInjectedGapPx(el) !== 0) {
            el.style.removeProperty(PAGE_GAP_CSS_VAR);
            TOP_GAP_CLASSES.forEach((cssClass) => el.classList.remove(cssClass));
        }
        if (getInjectedEndGapPx(el) !== 0) {
            el.style.removeProperty(PAGE_END_GAP_CSS_VAR);
            END_GAP_CLASSES.forEach((cssClass) => el.classList.remove(cssClass));
        }
        // RECUR-27604 §15 SF-1's own marker class (see `applyPageGapStyles`)
        // — re-derived fresh every pass, so a stale one left on ANY element
        // (not just a row the structured traversal walked) must go too.
        el.classList.remove(plateEditorStyles.pageCellSegRowActive);
    });
}

function stripPageGapStyles(allBlocks: HTMLElement[], contentEl: HTMLElement): void {
    allBlocks.forEach((block) => {
        stripOneGapVar(block, PAGE_GAP_CSS_VAR, getInjectedGapPx, plateEditorStyles.pageGapBefore);
        // RECUR-27604 §13.3(b): also strip the page-END pair — every
        // measurement pass must see a DOM equally free of top AND end gaps,
        // for the same reason §8.6's row branch below strips row gaps.
        stripOneGapVar(block, PAGE_END_GAP_CSS_VAR, getInjectedEndGapPx, plateEditorStyles.pageEndGapAfter);
        // RECUR-27604 §8.6 — row branch: also strip this block's own table's
        // rows (if any), so every pass still measures a fully gap-free DOM
        // for rows exactly as it already does for blocks.
        const table = findTableElement(block);
        if (!table) return;
        Array.from(table.rows).forEach((row) => {
            stripOneGapVar(row, PAGE_GAP_CSS_VAR, getInjectedGapPx, plateEditorStyles.pageRowGapBefore);
            stripOneGapVar(row, PAGE_END_GAP_CSS_VAR, getInjectedEndGapPx, plateEditorStyles.pageRowEndGapAfter);
            // onepager-pdf-orphan-blank-page: undo the fixed-height-row slot grow
            // so every measure sees the row at its ORIGINAL inline height, not the
            // grown calc — the grow (applyPageGapStyles) is a post-measure paint
            // only, exactly like the gap vars stripped just above.
            if (row.dataset.slotBaseHeight != null) {
                row.style.height = row.dataset.slotBaseHeight;
                row.style.removeProperty(SLOT_BASE_HEIGHT_VAR);
                delete row.dataset.slotBaseHeight;
            }
            // RECUR-27604 §15.6/OPTIONAL — mid-cell (rowSegment) level: every
            // cell's own Slate children (not just the current PRIMARY cell's
            // — a primary-cell-selection flip across passes must never leave
            // a stale gap on the cell that stopped being primary), so every
            // measurement pass still sees a DOM fully free of cell-paragraph
            // gaps too, same guarantee §8.6 already gives whole rows.
            Array.from(row.cells).forEach((cell) => {
                getSlateCellChildren(cell).forEach((child) => {
                    stripOneGapVar(child, PAGE_GAP_CSS_VAR, getInjectedGapPx, plateEditorStyles.pageCellSegGapBefore);
                    stripOneGapVar(child, PAGE_END_GAP_CSS_VAR, getInjectedEndGapPx, plateEditorStyles.pageCellSegEndGapAfter);
                });
            });
            // RECUR-27604 §15 SF-1 — the sibling vertical-align mitigation's
            // own marker class (see `applyPageGapStyles`) is re-derived fresh
            // every pass from the CURRENT targets, so it must be stripped
            // here too rather than left to a stale value.
            row.classList.remove(plateEditorStyles.pageCellSegRowActive);
        });
    });
    // RECUR-27604 belt-and-suspenders: the structured walk above is correct
    // for the DOM shape it knows about, but a residual on any element OUTSIDE
    // that shape (proven live-DOM bug — see `sweepStripResidualGapStyles`'s
    // own doc) would otherwise survive to inflate the measurement. Run last,
    // over the whole content root, so no shape gap in the walk above can ever
    // leave a spacer behind.
    sweepStripResidualGapStyles(contentEl);
}

/**
 * Renders each target's gap as REAL vertical space directly on its live
 * block (RECUR-27304 gap-display) — margin-top driven by a CSS custom
 * property + class (see `PlateEditor.module.scss`'s `.pageGapBefore`), never
 * a Slate transform and never an inserted DOM node, so the document
 * value / save-safety guarantee is untouched. Every top-level block in
 * `allBlocks` that ISN'T a current target, OR whose `gapPx` is exactly 0 (a
 * page that filled to capacity, nothing to carry forward), has its property +
 * class removed — so a block that stops being a page start (or never had any
 * leftover space to begin with) never carries a stale/empty gap.
 *
 * RECUR-27304 "one consistent marker": `--page-gap` now carries the RAW
 * `gapPx` alone — the constant gutter this used to add on top of every
 * target (even a zero-gap one) was removed, since the editor no longer draws
 * any band/label there (see `PageBoundaryOverlay`). `getInjectedGapPx`'s
 * normalization (pageStartUnits.ts) keeps subtracting back out exactly
 * whatever total is currently painted, so idempotency is unaffected by this
 * change — it was already generic over "however much is currently painted".
 *
 * RECUR-27604 phase 4 (mock v2 revision); §11 (always-on, rev 6): `bandSlotPx`
 * adds the page-break slot's own per-page PAINTED addend
 * (`EDITOR_PAGE_BREAK_SLOT_PX`) on top of every target's `gapPx` (see
 * `PageBreakSlotOverlay` and this file's `liveBoundaries` map below for the
 * matching seam-position adjustment). As of §11 this addend is driven by
 * `slotActive` (`showPageGaps` alone) rather than the logo-gated `bandActive`
 * — a hosted seam gets the full-width page-break chrome on every page-gap
 * document, logo or not. `computePageGapTargets` emits BOTH 'block'-kind
 * targets (element = the top-level block) AND hostable 'row'-kind targets
 * (element = the live `<tr>`, §8.5) — `gapByElement` doesn't need to branch on
 * kind itself since the addend is uniform; only the CSS class applied differs
 * (`.pageGapBefore` vs `.pageRowGapBefore`, §8.1), which the per-kind loops
 * below handle. `page1GapPx` (mock v2's page-1 clearance, 0 when the LOGO
 * band isn't active — page 1 stays logo-gated per §11.2, it is not a page
 * break) is a SEPARATE addend applied only to `allBlocks[0]` — page 1 is
 * never itself a `target.element` (the model never treats it as an interior
 * page-start), so it needs its own entry in the SAME map rather than flowing
 * through `targets`.
 */
/**
 * RECUR-27604 §13.3: `endTargets`/`endSlotPx` mirror `targets`/`bandSlotPx`
 * exactly — every `PageEndGapTarget`'s element gets the flat
 * `EDITOR_PAGE_END_SLOT_PX` addend (the model's own row-kind leftover is
 * never painted for the end slot either, same reasoning as §8.6 for the top
 * slot — there is no model-computed "trailing" leftover to add on top of).
 * Applied through the SAME `applyOrClean` helper, parametrized by CSS var +
 * class, so the top-gap and end-gap write/strip/idempotency logic can never
 * drift apart.
 */
function applyPageGapStyles(
    allBlocks: HTMLElement[],
    targets: PageGapTarget[],
    endTargets: PageEndGapTarget[],
    bandSlotPx: number,
    endSlotPx: number,
    page1GapPx: number,
): void {
    // RECUR-27604 phase 4/§11: every block-kind page start, and every
    // hostable row-kind one (§8), also carries the page-break slot's own
    // per-page addend, so the editor paints the SAME per-page rhythm the PDF
    // does (with a logo) or simply visualises the physical break (without
    // one — §11.1). A uniform addend is invisible to the model by
    // construction — `getInjectedGapPx` reads back exactly what was written
    // here and `buildAtomicUnits` subtracts that cumulative total from every
    // offsetTop before the model sees it (pageStartUnits.ts).
    const gapByElement = new Map(targets.map((target) => [target.element, target.gapPx + bandSlotPx] as const));
    if (allBlocks[0] && page1GapPx > 0) gapByElement.set(allBlocks[0], (gapByElement.get(allBlocks[0]) ?? 0) + page1GapPx);
    // RECUR-27604 §13.3 — the end-gap map: flat `endSlotPx` per target, no
    // model leftover to add (mirrors `gapPx: 0` for a top row-kind target,
    // §8.6) and no page-1 special case (page 1 is never the "page above" a
    // boundary — it has no predecessor).
    const endGapByElement = new Map(endTargets.map((target) => [target.element, endSlotPx] as const));
    // RECUR-27604 §15 SF-1 — every row currently hosting a mid-cell
    // ('rowSegment') top or end slot: the WHOLE row's cells (including the
    // primary cell — harmless, it's content-tall with no free space to
    // redistribute) are forced to `vertical-align: top` so a
    // middle/bottom-aligned SIBLING can't center its content into the masked
    // band (§13.1(c)'s pattern, one level deeper).
    const rowSegmentActiveRows = new Set<HTMLTableRowElement>();
    targets.forEach((target) => {
        if (target.kind !== 'rowSegment') return;
        const row = target.element.closest('tr');
        if (row) rowSegmentActiveRows.add(row);
    });
    endTargets.forEach((target) => {
        if (target.kind !== 'rowSegment') return;
        const row = target.element.closest('tr');
        if (row) rowSegmentActiveRows.add(row);
    });
    const applyOrClean = (
        el: HTMLElement,
        gapMap: Map<HTMLElement, number>,
        cssVar: string,
        read: (el: HTMLElement) => number,
        gapClass: string,
    ) => {
        const gapPx = gapMap.get(el);
        const currentGapPx = read(el);
        if (gapPx !== undefined && gapPx > 0) {
            if (Math.abs(gapPx - currentGapPx) < PAGE_GAP_REWRITE_EPSILON_PX) return;
            el.style.setProperty(cssVar, `${gapPx}px`);
            el.classList.add(gapClass);
        } else if (currentGapPx > 0) {
            el.style.removeProperty(cssVar);
            el.classList.remove(gapClass);
        }
    };
    allBlocks.forEach((block) => {
        applyOrClean(block, gapByElement, PAGE_GAP_CSS_VAR, getInjectedGapPx, plateEditorStyles.pageGapBefore);
        applyOrClean(block, endGapByElement, PAGE_END_GAP_CSS_VAR, getInjectedEndGapPx, plateEditorStyles.pageEndGapAfter);
        // RECUR-27604 §8.1/§8.6 — row branch: every row of this block's own
        // table is a cleanup candidate too, mirroring the block above — a
        // row that stopped being a target (or was skipped as un-hostable,
        // §8.5) must never carry a stale slot/class.
        const table = findTableElement(block);
        if (!table) return;
        Array.from(table.rows).forEach((row) => {
            applyOrClean(row, gapByElement, PAGE_GAP_CSS_VAR, getInjectedGapPx, plateEditorStyles.pageRowGapBefore);
            applyOrClean(row, endGapByElement, PAGE_END_GAP_CSS_VAR, getInjectedEndGapPx, plateEditorStyles.pageRowEndGapAfter);
            // onepager-pdf-orphan-blank-page: a fixed-height host row absorbs part
            // of the top-slot cell padding, so ALSO grow its inline height by that
            // slot (`gapByElement` holds `gapPx + bandSlotPx` for top-slot rows).
            // Delivered as a calc off --page-gap so it collapses to the base under
            // the export's `--page-gap: 0` (PDF untouched); the model subtracts the
            // same slot via getInjectedGapPx, so it still measures the ORIGINAL
            // fixed height. stripPageGapStyles restores the base before the next
            // measure; the dataset marker keeps this idempotent within a pass.
            const rowSlotPx = gapByElement.get(row);
            if (rowSlotPx && rowSlotPx > 0 && row.style.height && row.dataset.slotBaseHeight == null) {
                row.dataset.slotBaseHeight = row.style.height;
                row.style.setProperty(SLOT_BASE_HEIGHT_VAR, row.style.height);
                row.style.setProperty('height', `calc(var(${SLOT_BASE_HEIGHT_VAR}) + var(${PAGE_GAP_CSS_VAR}, 0px))`, 'important');
            }
            // RECUR-27604 §15.6/OPTIONAL — cell-child level: every cell's own
            // Slate children (not just the primary cell's), mirroring the
            // strip pass above 1:1 so a primary-cell-selection flip can never
            // leave a stale gap behind.
            Array.from(row.cells).forEach((cell) => {
                getSlateCellChildren(cell).forEach((child) => {
                    applyOrClean(child, gapByElement, PAGE_GAP_CSS_VAR, getInjectedGapPx, plateEditorStyles.pageCellSegGapBefore);
                    applyOrClean(
                        child,
                        endGapByElement,
                        PAGE_END_GAP_CSS_VAR,
                        getInjectedEndGapPx,
                        plateEditorStyles.pageCellSegEndGapAfter,
                    );
                });
            });
            if (rowSegmentActiveRows.has(row)) {
                row.classList.add(plateEditorStyles.pageCellSegRowActive);
            } else {
                row.classList.remove(plateEditorStyles.pageCellSegRowActive);
            }
        });
    });
}

/**
 * RECUR-27852 — the top host's rendered anchor Y (contentEl-relative px): the Y
 * where its injected top slot BEGINS, so the overlay can paint the seam / top
 * mask / logo at the ACTUAL rendered padding rather than the model's
 * (drift-prone) seam. For a `'block'` start the 90px slot is the block's own
 * `margin-top`, so the slot begins `EDITOR_PAGE_BREAK_SLOT_PX` ABOVE the
 * block's border box; for a `'row'`/`'rowSegment'` start the slot is cell
 * `padding-top` INSIDE the row, so the row's own border-top IS the slot top.
 * `undefined` when there's no measurable layout (jsdom `getBoundingClientRect`
 * all-zero) — the caller then keeps the model seam and every boundary test
 * stays unchanged.
 */
function measureTopSlotAnchor(
    element: HTMLElement,
    kind: 'block' | 'row' | 'rowSegment',
    contentTop: number,
    hasLayout: boolean,
): number | undefined {
    if (!hasLayout) return undefined;
    const top = element.getBoundingClientRect().top - contentTop;
    return kind === 'block' ? top - EDITOR_PAGE_BREAK_SLOT_PX : top;
}

/**
 * RECUR-27852 — the end host's rendered anchor Y (contentEl-relative px): the
 * Y where its injected end slot ENDS — the host's own border-BOTTOM, which
 * already includes the injected `padding-bottom` (row) / `border-bottom`
 * (block). The overlay paints the end mask up to here so it covers the real
 * bottom margin and never the last live line, independent of the model seam.
 * `undefined` with no measurable layout (jsdom), same as the top anchor.
 */
function measureEndSlotAnchor(element: HTMLElement, contentTop: number, hasLayout: boolean): number | undefined {
    if (!hasLayout) return undefined;
    return element.getBoundingClientRect().bottom - contentTop;
}

export function usePageBoundaries(
    containerEl: HTMLElement | null,
    enabled: boolean,
    reservedPageTopPx = 0,
    editorBandEnabled = true,
): PageBoundariesWithBand {
    const [state, setState] = useState<PageBoundariesWithBand>(EMPTY_BOUNDARIES_WITH_BAND);
    const lastKeyRef = useRef('');

    // Shared by every early-out below (RECUR-27304 corrective): resets BOTH
    // the guard key and the returned state, so a remount that finds nothing
    // to measure never leaves a PRIOR editor instance's boundaries showing.
    const resetToEmpty = () => {
        lastKeyRef.current = '';
        setState(EMPTY_BOUNDARIES_WITH_BAND);
    };

    useEffect(() => {
        if (!enabled) {
            resetToEmpty();
            return;
        }

        const contentEl = containerEl?.querySelector<HTMLElement>(CONTENT_SELECTOR);
        // Defensive: jsdom/older browsers may not implement ResizeObserver at
        // all (mirrors the same guard in ProgressBorder.tsx). Reset (not a
        // bare early-out) — `PlateErrorBoundary`'s crash-recovery remount
        // swaps in a brand-new content-wrap element (see this file's module
        // doc), and if ITS content isn't found here, a stale seam from the
        // OLD instance must not linger on screen.
        if (!contentEl || typeof ResizeObserver === 'undefined') {
            resetToEmpty();
            return;
        }

        let rafId: number | null = null;
        let idleId: number | null = null;
        const measure = () => {
            rafId = null;
            const blocks = getTopLevelBlocks(contentEl);
            stripPageGapStyles(blocks, contentEl);

            // RECUR-27604 §11 (always-on full-width page-start break, rev 6):
            // the model's own `reservedPageTopPx` reservation (page CAPACITY —
            // see pageStartUnits.ts's module doc) stays active regardless of
            // either flag below, so editor seams keep matching the PDF even
            // with the logo/band preview switched off.
            //
            // The page-break CHROME itself — the painted slot addend, `bands`
            // emission, the mask/edges (PageBreakSlotOverlay), and the
            // overlay-only row-offset correction (pageStartUnits.ts's
            // `applyRowOffsetCorrection`, which must be on wherever a mask
            // anchors) — is unconditionally active: it is driven by `enabled`
            // (the caller's `showPageGaps`) ALONE, not by the logo flags, and
            // `measure()` only ever runs while `enabled` is true (see the
            // early-out above).
            //
            // `bandActive` is now narrower: it gates ONLY the page-1 injected
            // clearance (page 1 is not a page break — its own 21px exists
            // solely to clear the logo, so it stays logo-gated) and the logo
            // `<img>` itself (`PlateEditor.tsx` passes
            // `logoUrl={bandActive ? pnoLogoUrl : undefined}`).
            const bandActive = editorBandEnabled && reservedPageTopPx > 0;
            const bandSlotPx = EDITOR_PAGE_BREAK_SLOT_PX;
            // RECUR-27604 §13.3(c): the page-END slot is unconditionally
            // active too — same reasoning as the top slot (§11.1/§13.3(c)): a
            // page-bottom margin that appeared only for logo PNOs would make
            // identical documents paginate-looking differently per config.
            const endSlotPx = EDITOR_PAGE_END_SLOT_PX;
            const page1GapPx = bandActive ? PAGE1_INJECTED_GAP_PX : 0;

            const width = contentEl.offsetWidth;
            // RECUR-27604 §11.3 — the mask's own box: the CONTENT COLUMN
            // (`pageLeft`/`pageWidth`), NOT the host table's box (`hostLeft`/
            // `hostWidth` below, still used for the edges) — measured once
            // per pass, in the SAME contentEl-relative coordinate space `top`
            // is already in, and carried on each row-kind slot (§11.6's
            // change-key note: a window resize changes this with every
            // boundary Y unchanged, so it must be quantized into `nextKey`
            // below same as `hostLeft`/`hostWidth` already are). `clientWidth`
            // and computed padding are 0 in jsdom (no real layout there) —
            // `PageBreakSlotOverlay` is responsible for falling back to the
            // host box whenever `pageWidth <= 0`. Reuses `getContentColumnBox`
            // (pageStartUnits.ts) — the SAME box §12.3's crossing-filter
            // measures — so the mask and that filter measure from ONE box.
            const { left: pageLeft, width: pageWidth } = getContentColumnBox(contentEl);
            const decision = computePageBoundaries(blocks, width, reservedPageTopPx, true);
            // Sentry invariant input (paginationTelemetry): the gap-free
            // content bottom MUST be read here — after stripPageGapStyles,
            // before applyPageGapStyles — to share `decision`'s coordinate
            // space.
            const normalizedContentBottomPx = measureNormalizedContentBottomPx(contentEl, blocks);
            // Always re-applied (RECUR-27304 gap-display) — see this file's
            // module doc for why this sits outside the "did it change" guard
            // below.
            const targets = computePageGapTargets(blocks, width, reservedPageTopPx);
            // RECUR-27604 §13.3: the page-END mirror of `targets`, computed
            // against the SAME gap-free (post-strip) DOM in the SAME pass —
            // cheap (no gap applied between the two calls).
            const endTargets = computePageEndGapTargets(blocks, width, reservedPageTopPx);
            applyPageGapStyles(blocks, targets, endTargets, bandSlotPx, endSlotPx, page1GapPx);

            // RECUR-27604 §8/N-5 ("one predicate, one place"): built straight
            // FROM the targets just painted above (each carries its own
            // `boundaryIndex` — pageStartUnits.ts's `PageGapTarget`) instead
            // of re-deriving §8.5's row-hostability predicate a second time
            // here — so paint, geometry and `bands`/`bandTops` below can
            // never disagree by construction. Kept as the full target (not
            // just its gapPx) so the `bands` derivation below can also read
            // `target.kind`/`target.element` without a second lookup.
            const targetByBoundaryIndex = new Map(targets.map((target) => [target.boundaryIndex, target] as const));
            // RECUR-27604 §13.3 — same "one predicate, one place" discipline
            // for the end-gap map.
            const endTargetByBoundaryIndex = new Map(endTargets.map((target) => [target.boundaryIndex, target] as const));

            // RECUR-27852 — measured anchoring: `getBoundingClientRect` is
            // all-zero in jsdom (no layout), so guard every measured override
            // on real layout and fall back to the model seam there, keeping
            // the pure-model tests untouched. `contentTop` is the origin every
            // measured anchor is made relative to (matching the seam's own
            // contentEl-relative space).
            const contentClientRect = contentEl.getBoundingClientRect();
            const hasLayout = contentClientRect.height > 0;
            const contentTop = contentClientRect.top;
            let cumulativeAppliedGapPx = 0;
            const liveBoundaries = decision.boundaries.map((gapFreeY, i) => {
                const injectedHere = targetByBoundaryIndex.has(i) ? bandSlotPx : 0;
                // RECUR-27604 §13.3(a): the end slot belongs to the PAGE
                // ABOVE this boundary — it shifts the cumulative total the
                // SAME way `decision.gaps[i]` does (it's more "unused" space
                // painted on that same outgoing page), but — like the top
                // slot — is never itself subtracted back out of the seam
                // (the seam still marks the top of the TOP slot, not the
                // bottom of the end slot).
                const endInjectedHere = endTargetByBoundaryIndex.has(i) ? endSlotPx : 0;
                cumulativeAppliedGapPx += decision.gaps[i] + endInjectedHere + injectedHere;
                // The seam marks the TOP OF THE BAND SLOT — the true page top.
                // The gap-model expression below (unchanged) is exact only
                // while every prior page filled to the same capacity the model
                // reserved; on an image/table-heavy document that assumption
                // slips and the seam settles a few rows off the real break
                // (RECUR-27852). When there's a live top host we therefore
                // PREFER its MEASURED slot anchor — the Y where the injected
                // top padding actually rendered — so the seam, masks and logo
                // pin to the real page-break gap instead of a drifted model
                // value. One formula covers both vehicles: for a 'block' start
                // the 90px slot is the block's own margin-top (ABOVE its box);
                // for a 'row' start it is cell padding INSIDE the row (the
                // row's own top IS the slot top) — see `measureTopSlotAnchor`.
                // No top host (an 'endOnly' or un-hostable boundary), or no
                // measurable layout (jsdom): keep the model seam.
                const topTarget = targetByBoundaryIndex.get(i);
                const measuredSeam = topTarget ? measureTopSlotAnchor(topTarget.element, topTarget.kind, contentTop, hasLayout) : undefined;
                return measuredSeam ?? gapFreeY + cumulativeAppliedGapPx - injectedHere;
            });
            // Every boundary that actually received a TOP and/or END slot, as
            // a `PageBreakSlot` — the ONLY thing `PageBreakSlotOverlay` needs
            // to know where (and, for a mid-table seam, how WIDE) to paint
            // the slot's mask/edges past page 1 (§4/§8.4/§11.3/§13.3); it
            // never re-checks kind or DOM state itself. The host `<table>`'s
            // own offsetLeft/offsetWidth are ALREADY contentEl-relative: its
            // `.tableScroll` wrapper is `position: static` and carries no
            // border of its own (the visible border lives on the cells,
            // bounded by the `<table>` box itself), so no offsetParent hop
            // lands between it and `.content` the way a `<tr>`'s does — no
            // `cumulativeOffsetTop`-style walk needed.
            //
            // RECUR-27604 §11: population is driven by `slotActive` alone now
            // (unconditionally true here — see this function's own doc
            // comment above) — `bands` populates on EVERY hosted seam of a
            // page-gap document, logo or not; the OLD `!bandActive` gate is
            // gone. A 'block'-kind target is created unconditionally by
            // `computePageGapTargets` (even a 0-reservation call), so
            // `!target && !endTarget` is what decides "was NOTHING injected
            // here" now — a boundary can have an end slot with no top slot
            // (or vice versa; the two hostability checks are independent),
            // so a missing top target no longer drops the entry outright
            // (§13.3, `kind: 'endOnly'`).
            const bands: PageBreakSlot[] = liveBoundaries
                .map((top, i): PageBreakSlot | null => {
                    const target = targetByBoundaryIndex.get(i);
                    const endTarget = endTargetByBoundaryIndex.get(i);
                    if (!target && !endTarget) return null;
                    // RECUR-27604 §15.4 — a 'rowSegment' target/endTarget is
                    // mapped to overlay-kind 'row' exactly like a mid-table
                    // 'row' one: `.closest('table')` resolves the SAME host
                    // table for a cut PARAGRAPH as it does for a `<tr>` (the
                    // table's offsetLeft/offsetWidth are already
                    // contentEl-relative either way) — zero PageBreakSlotOverlay
                    // change needed.
                    const endSlot: PageBreakSlot['endSlot'] = endTarget
                        ? endTarget.kind === 'row' || endTarget.kind === 'rowSegment'
                            ? (() => {
                                  const endTable = endTarget.element.closest('table');
                                  return { kind: 'row' as const, hostLeft: endTable?.offsetLeft, hostWidth: endTable?.offsetWidth };
                              })()
                            : { kind: 'block' as const }
                        : undefined;
                    // RECUR-27852 — the end host's MEASURED bottom anchor: where
                    // its injected end slot actually rendered, so the end mask
                    // pins to the real bottom margin (see `PageBreakSlot.endSlotSeam`).
                    const endSlotSeam = endTarget ? measureEndSlotAnchor(endTarget.element, contentTop, hasLayout) : undefined;
                    if (target?.kind === 'row' || target?.kind === 'rowSegment') {
                        const table = target.element.closest('table');
                        return {
                            top,
                            kind: 'row',
                            hostLeft: table?.offsetLeft,
                            hostWidth: table?.offsetWidth,
                            pageLeft,
                            pageWidth,
                            endSlot,
                            endSlotSeam,
                        };
                    }
                    if (target) return { top, kind: 'block', endSlot, endSlotSeam };
                    // No top slot at this boundary, but an end slot exists —
                    // e.g. a hostable outgoing row/block followed by a
                    // 'line'-kind, crossed, or un-hostable incoming start.
                    return { top, kind: 'endOnly', endSlot, endSlotSeam };
                })
                .filter((band): band is PageBreakSlot => band !== null);
            const bandTops = bands.filter((band) => band.kind !== 'endOnly').map((band) => band.top);
            const next: PageBoundariesWithBand = { ...decision, boundaries: liveBoundaries, bandTops, bands, pageLeft, pageWidth };

            // The paginator-accurate model's boundaries aren't an arithmetic
            // progression (Delta v2) — an edit can shift every boundary Y
            // after it without changing the total COUNT, so the guard key
            // must cover the actual positions, not just `count:pageHeightPx`.
            // `kinds` is included too (RECUR-27304 Word-frame visuals) — a
            // boundary can in principle swap kind (e.g. 'row' <-> 'block')
            // while its Y and gap coincidentally stay the same, and the
            // overlay renders each kind differently. `bandTops` (length +
            // rounded values, RECUR-27604 §1.4) is MANDATORY, not cosmetic:
            // the row-skip predicate (§8.5) can flip with ZERO geometry
            // change the moment `TableRowElement` writes an inline `height`
            // on a `<tr>` (tableElements.tsx:162) — without this term, a
            // stale band would keep painting over a row that no longer has a
            // slot, because nothing else in the key would have changed. The
            // mid-table mask adds the same hazard one level deeper: a table
            // resize/column-indent can change `hostLeft`/`hostWidth` with
            // every boundary Y unchanged, so `bands`' rounded host values are
            // folded in too — otherwise a resized table's mask would keep
            // painting at the old (now wrong) position/size. RECUR-27604 §11.6:
            // `pageLeft`/`pageWidth` join them for the same reason — a window
            // resize changes the CONTENT column with every boundary Y and host
            // box unchanged, and a stale mask would otherwise keep painting at
            // the old width. RECUR-27604 §13.3(a): the end-slot values join
            // them for the identical reason — a page whose last unit becomes
            // un-hostable (the same `row.style.height`/crossing flips §8.5/§12
            // already guard for the top slot) must re-render even though
            // `boundaries`/`kinds`/`bands`' TOP fields are all unchanged, and
            // `endSlot`'s own host box can independently resize.
            const nextKey = `${Math.round(next.pageHeightPx)}:${next.boundaries.map((b) => Math.round(b)).join(',')}:${next.gaps
                .map((g) => Math.round(g))
                .join(',')}:${next.kinds.join(',')}:${next.bandTops.length}:${next.bandTops
                .map((t) => Math.round(t))
                .join(',')}:${Math.round(next.pageLeft)}/${Math.round(next.pageWidth)}:${next.bands
                .map(
                    (b) =>
                        `${b.kind}:${b.hostLeft != null ? Math.round(b.hostLeft) : ''}/${
                            b.hostWidth != null ? Math.round(b.hostWidth) : ''
                        }/${b.pageLeft != null ? Math.round(b.pageLeft) : ''}/${b.pageWidth != null ? Math.round(b.pageWidth) : ''}/${
                            b.endSlot
                                ? `${b.endSlot.kind}:${b.endSlot.hostLeft != null ? Math.round(b.endSlot.hostLeft) : ''}/${
                                      b.endSlot.hostWidth != null ? Math.round(b.endSlot.hostWidth) : ''
                                  }`
                                : ''
                        }/${
                            // RECUR-27852: the measured end anchor MUST join the change-key — a
                            // reflow can move where the end host renders (and thus the end mask)
                            // with `top`/`kind`/host box all unchanged, so without this a stale
                            // end mask would persist. The measured TOP anchor is already covered
                            // by `boundaries`/`bandTops` above. Matches the §13.3(a) reasoning.
                            b.endSlotSeam != null ? Math.round(b.endSlotSeam) : ''
                        }`,
                )
                .join(',')}`;
            if (nextKey === lastKeyRef.current) return;
            lastKeyRef.current = nextKey;
            // Sentry: refresh pagination context, check the planned-page
            // invariants, feed the oscillation detector. Only on ACCEPTED
            // (changed) measurements; never throws (paginationTelemetry).
            reportPaginationMeasurement({ decision, normalizedContentBottomPx, blockCount: blocks.length, boundaryKey: nextKey });
            setState(next);
        };

        // Coalesces a burst of notifications — from EITHER observer below,
        // possibly both in the same frame (e.g. an edit that both reflows
        // text AND resizes the container) — into a single measurement per
        // animation frame.
        const scheduleMeasure = () => {
            // An immediate schedule supersedes a pending text-edit debounce — the
            // structural change it represents must not wait out that window.
            if (idleId != null) {
                window.clearTimeout(idleId);
                idleId = null;
            }
            if (rafId != null) return;
            rafId = requestAnimationFrame(measure);
        };

        // Text-only edits: trail the burst by TEXT_EDIT_MEASURE_IDLE_MS, then take the
        // same rAF-coalesced path. Re-armed on every keystroke, so a continuous typist
        // pays one measure per pause instead of one per frame.
        const scheduleMeasureAfterTextIdle = () => {
            if (rafId != null) return;
            if (idleId != null) window.clearTimeout(idleId);
            idleId = window.setTimeout(() => {
                idleId = null;
                rafId = requestAnimationFrame(measure);
            }, TEXT_EDIT_MEASURE_IDLE_MS);
        };

        // Width changes (window/column resize) move every seam immediately.
        // Height-only changes are almost always typing wrap — same idle window
        // as characterData, or the mutation debounce is wasted: a wrap still
        // notifies ResizeObserver and we'd measure every frame while typing.
        // Empty/unknown entries stay immediate so tests and odd browsers that
        // omit contentRect don't silently drop a measure.
        let lastWidthPx = contentEl.offsetWidth;
        const observer = new ResizeObserver((entries) => {
            const rect = entries[0]?.contentRect;
            if (!rect) {
                scheduleMeasure();
                return;
            }
            const widthChanged = Math.abs(rect.width - lastWidthPx) >= 1;
            lastWidthPx = rect.width;
            if (widthChanged) scheduleMeasure();
            else scheduleMeasureAfterTextIdle();
        });
        observer.observe(contentEl);

        // RECUR-27304 corrective: ResizeObserver only fires on a content-BOX
        // size change — an edit that shifts where a page breaks WITHOUT
        // changing the container's own box (e.g. text reflowing as
        // characters are typed/deleted inside an unchanged-height paragraph)
        // never notifies it, leaving the overlay stale until some LATER,
        // size-changing edit happens to re-trigger it. A MutationObserver on
        // the same content root catches those too, feeding the SAME
        // rAF-coalesced `measure` path.
        //
        // Scoped to ONLY `{ childList, subtree, characterData }` — NEVER
        // `attributes`: `applyPageGapStyles`/`stripPageGapStyles` (above)
        // write/remove the `--page-gap` style property + `pageGapBefore`
        // class on every pass, so observing attribute mutations would make
        // this observer notify itself on its own write, every single pass
        // (see this file's module doc). Structural/text edits only.
        //
        // Routed by kind: an ELEMENT childList (pasted block, deleted paragraph,
        // new table row) measures immediately. characterData AND childList that
        // only add/remove #text nodes (Slate's typing path) debounce — otherwise
        // a CAM of ~80 tables / ~4k cells re-measures on every keystroke.
        // Empty/unknown record sets stay immediate so a caller firing the
        // callback with no records still measures now.
        const mutationObserver = new MutationObserver((records) => {
            if (records.length === 0) {
                scheduleMeasure();
                return;
            }
            let elementNodes = 0;
            let unknownChildList = false;
            for (const record of records) {
                if (record.type !== 'childList') continue;
                if (!record.addedNodes || !record.removedNodes) {
                    unknownChildList = true;
                    break;
                }
                elementNodes += countElementNodes(record.addedNodes) + countElementNodes(record.removedNodes);
            }
            if (unknownChildList) {
                scheduleMeasure();
                return;
            }
            if (elementNodes === 0) {
                scheduleMeasureAfterTextIdle();
                return;
            }
            if (elementNodes >= BULK_ELEMENT_MEASURE_IDLE) {
                scheduleMeasureAfterTextIdle();
                return;
            }
            scheduleMeasure();
        });
        mutationObserver.observe(contentEl, { childList: true, subtree: true, characterData: true });

        measure();

        return () => {
            if (rafId != null) cancelAnimationFrame(rafId);
            if (idleId != null) window.clearTimeout(idleId);
            observer.disconnect();
            mutationObserver.disconnect();
        };
    }, [containerEl, enabled, reservedPageTopPx, editorBandEnabled]);

    return state;
}
